// Types
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardBody, CardFooter } from 'components/Card';
import { EImpactedOjbectType } from 'constants/PaddingRule.constants';
import _ from 'lodash';
import { selectFieldsOfType } from 'slices/field.slice';
// Slices
import { selectObjectTypesList } from 'slices/objectTypes.slice';
// Utils
import {
  getFieldsForSelectedType,
  getSelectOptionsForCategoryField,
} from 'utils/paddingUtils';
import { EFieldType } from '@timeedit/types/lib/enums';
import type { TObjectFilterCriteria } from '@timeedit/types/lib/types';
import intl from '../../i18n/intl';
import ExtIdSelect from './ExtIdSelect';
// Components
import FieldValueSelect from './FieldValueSelect';
import ObjectTypeSelect from './ObjectTypeSelect';
import './index.scss';

const language: any = intl.messages;
export interface ObjectCriteriaProps {
  impactedObjects: TObjectFilterCriteria[];
  objectsOnReservation: TObjectFilterCriteria[];
  onChange: (val: {
    impactedObjects: TObjectFilterCriteria[];
    objectsOnReservation: TObjectFilterCriteria[];
  }) => void;
  isExpand: boolean;
  showImpact?: boolean;
}

type TImpactedObjectFilterCriteria = TObjectFilterCriteria & {
  impacted?: boolean;
};

const ObjectCriteria = ({
  impactedObjects,
  objectsOnReservation,
  onChange,
  isExpand,
  showImpact = true,
}: ObjectCriteriaProps) => {
  const types = useSelector(selectObjectTypesList);
  const typeOptions = useMemo(
    () =>
      types.map((type) => ({
        value: type.id.toString(),
        label: type.name,
      })),
    [types]
  );
  const categoryFields = useSelector(selectFieldsOfType(EFieldType.CATEGORY));
  const [objectCriterias, setObjectCriterias] = useState<
    TImpactedObjectFilterCriteria[]
  >([]);
  useEffect(() => {
    setObjectCriterias([
      ...impactedObjects.map((obj) => ({ ...obj, impacted: true })),
      ...objectsOnReservation.map((obj) => ({ ...obj, impacted: false })),
    ]);
  }, [impactedObjects, objectsOnReservation]);

  const objectTypes: EImpactedOjbectType[] = useMemo(
    () =>
      objectCriterias.map((object) => {
        if (object.objectExtIds) return EImpactedOjbectType.EXT_IDS;
        if (object.fieldCriteria) return EImpactedOjbectType.FIELD_VALUE;
        return EImpactedOjbectType.OBJECT_TYPE;
      }),
    [objectCriterias]
  );

  const changeRule = (objects: TImpactedObjectFilterCriteria[]) => {
    onChange({
      impactedObjects: objects
        .filter((obj) => obj.impacted)
        .map((obj) => {
          const { impacted, ...res } = obj;
          return res;
        }),
      objectsOnReservation: objects
        .filter((obj) => !obj.impacted)
        .map((obj) => {
          const { impacted, ...res } = obj;
          return res;
        }),
    });
  };

  const onDelete = (index: number) => {
    const newObjects = objectCriterias.filter((_val, idx) => idx !== index);
    changeRule(newObjects);
  };

  const onAdd = (impactedObjectType: EImpactedOjbectType) => {
    let newObject: TImpactedObjectFilterCriteria;
    switch (impactedObjectType) {
      case EImpactedOjbectType.EXT_IDS:
        newObject = {
          objectExtIds: [],
          impacted: true,
        };
        break;
      case EImpactedOjbectType.OBJECT_TYPE:
        newObject = {
          typeId: 0,
          impacted: true,
        };
        break;
      case EImpactedOjbectType.FIELD_VALUE:
        newObject = {
          typeId: 0,
          fieldCriteria: {
            fieldId: 0,
            fieldValues: [],
          },
          impacted: true,
        };
        break;
    }
    changeRule([...objectCriterias, newObject]);
  };

  const onCriteriaUpdated = (
    index: number,
    updatedValues: Record<string, any>
  ) => {
    const newObjects = [...objectCriterias];
    const impactedObject = newObjects[index];
    newObjects[index] = {
      ...impactedObject,
      ...updatedValues,
    };
    changeRule(newObjects);
  };

  return (
    <div className="object-criteria">
      <Card
        title="Objects"
        tooltip="Impacted objects"
        footer={
          <CardFooter.DropdownMenu
            menu={{
              items: Object.values(EImpactedOjbectType).map((type) => ({
                key: type,
                label: language[type],
              })),
              onClick: (item) => onAdd(item.key as EImpactedOjbectType),
            }}
          />
        }
      >
        <CardBody.VirtualList data={objectTypes}>
          {(item, index) => {
            const impactedObject = objectCriterias[index];
            switch (item) {
              case EImpactedOjbectType.EXT_IDS:
                return (
                  <ExtIdSelect
                    index={index}
                    impactedObject={impactedObject}
                    onCriteriaUpdated={onCriteriaUpdated}
                    showImpact={showImpact}
                    typeOptions={typeOptions}
                    onDelete={() => onDelete(index)}
                  />
                );
              case EImpactedOjbectType.FIELD_VALUE:
                return (
                  <FieldValueSelect
                    index={index}
                    impactedObject={impactedObject}
                    onCriteriaUpdated={onCriteriaUpdated}
                    showImpact={showImpact}
                    typeOptions={typeOptions}
                    fieldOptions={getFieldsForSelectedType(
                      impactedObject.typeId,
                      types,
                      categoryFields
                    ).map((field) => ({
                      label: field.name,
                      value: field.id.toString(),
                    }))}
                    fieldValueOptions={getSelectOptionsForCategoryField(
                      impactedObject?.fieldCriteria?.fieldId,
                      categoryFields
                    )}
                    onDelete={() => onDelete(index)}
                  />
                );
              case EImpactedOjbectType.OBJECT_TYPE:
                return (
                  <ObjectTypeSelect
                    index={index}
                    impactedObject={impactedObject}
                    onCriteriaUpdated={onCriteriaUpdated}
                    showImpact={showImpact}
                    typeOptions={typeOptions}
                    onDelete={() => onDelete(index)}
                  />
                );
            }
            return null;
          }}
        </CardBody.VirtualList>
      </Card>
    </div>
  );
};

export default ObjectCriteria;
